/**
 * DENET — bildirimler
 *
 * Sepete ekleme hatası şimdiye kadar sessizce yutuluyordu: `cartError`
 * yayınlanıyor ama kimse dinlemiyordu. Müşteri düğmeye basıyor, hiçbir şey
 * olmuyor, tekrar basıyor.
 *
 * Kurallar:
 *  - Hata her zaman görünür ve elle kapatılana kadar durur.
 *  - Başarı kısa sürer; sepet çekmecesi zaten açıldıysa hiç gösterilmez —
 *    aynı bilgiyi iki kez söylemenin anlamı yok.
 *  - Aynı mesaj üst üste gelirse tekrarlanmaz, sayaç artar.
 */

import { DenetElement, define, on, EVENTS } from '@denet/core';

const LIFE = 4000;

class DnToast extends DenetElement {
  mounted() {
    this.list = this.ref('list');
    this.drawerOpen = false;

    on(EVENTS.drawerOpen, () => { this.drawerOpen = true; }, { signal: this.signal });
    on(EVENTS.drawerClose, () => { this.drawerOpen = false; }, { signal: this.signal });

    on(EVENTS.cartUpdated, (e) => {
      /* `silent` sepetin kendi içindeki adet güncellemesi: bildirim gereksiz. */
      if (e.detail?.silent || this.drawerOpen) return;
      this.show(this.dataset.added || 'Sepete eklendi', 'ok');
    }, { signal: this.signal });

    on(EVENTS.cartError, (e) => {
      const msg = e.detail?.message || this.dataset.error || 'İşlem tamamlanamadı';
      this.show(msg, 'error');
    }, { signal: this.signal });

    /* Başka bileşenler de kullanabilsin. */
    on('denet:toast', (e) => {
      if (e.detail?.message) this.show(e.detail.message, e.detail.tone || 'ok', e.detail.action);
    }, { signal: this.signal });
  }

  /**
   * @param {string} message
   * @param {'ok' | 'error'} tone
   */
  show(message, tone = 'ok', action = null) {
    if (!this.list) return;

    /* Aynı mesaj arka arkaya gelirse yeni satır açma, sayacı artır. */
    const last = this.list.lastElementChild;
    if (!action && last && last.dataset.message === message) {
      const n = Number(last.dataset.count || 1) + 1;
      last.dataset.count = String(n);
      const badge = last.querySelector('[data-count]');
      if (badge) { badge.textContent = `×${n}`; badge.hidden = false; }
      return;
    }

    const el = document.createElement('div');
    el.className = `dn-toast dn-toast--${tone}`;
    el.dataset.message = message;
    el.setAttribute('role', tone === 'error' ? 'alert' : 'status');
    el.innerHTML = `
      <span class="dn-toast__icon" aria-hidden="true"></span>
      <span class="dn-toast__text">${message.replace(/[<>&]/g, '')}</span>
      <span class="dn-toast__count" data-count hidden></span>
      <button class="dn-toast__close" type="button" aria-label="${this.dataset.close || 'Kapat'}">×</button>`;

    const remove = () => {
      el.classList.add('is-out');
      el.addEventListener('animationend', () => el.remove(), { once: true });
      window.setTimeout(() => el.remove(), 400);
    };

    el.querySelector('.dn-toast__close')?.addEventListener('click', remove);

    /* İsteğe bağlı eylem ("Geri al"): bir kez çalışır, bildirim kapanır. */
    if (action?.label && typeof action.run === 'function') {
      const dugme = document.createElement('button');
      dugme.type = 'button';
      dugme.className = 'dn-toast__eylem';
      dugme.textContent = action.label;
      dugme.addEventListener('click', () => { dugme.disabled = true; action.run(); remove(); }, { once: true });
      el.querySelector('.dn-toast__text')?.after(dugme);
    }
    this.list.appendChild(el);

    /* Hata kendiliğinden kapanmaz: kullanıcı görmeden gitmemeli. */
    /* Eylemli bildirim biraz daha uzun kalır: düğmeye uzanacak zaman. */
    if (tone !== 'error') window.setTimeout(remove, action ? LIFE * 2 : LIFE);
  }
}

define('dn-toast', DnToast);
